import { useState, ChangeEvent } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@mui/material";
import SvgIcon from "@mui/material/SvgIcon";
import Checkbox from "@mui/material/Checkbox";

import "./style.scss";
import { ReactComponent as EyeIcon } from "../../../assets/eyeIcon.svg";
import { ReactComponent as EyeOffIcon } from "../../../assets/eyeOffIcon.svg";
import { ReactComponent as CheckboxIcon } from "../../../assets/checkbox.icon.svg";
import { ReactComponent as CheckboxCheckedIcon } from "../../../assets/checkbox-checked.icon.svg";
import { ReactComponent as GoogleIcon } from "../../../assets/googleIcon.svg";
import { ReactComponent as MicrosoftIcon } from "../../../assets/microsoftIcon.svg";
import { AuthErrorMessage } from "../../../common/enums/auth-error-message.enum";
import { FirebaseErrorCode } from "../../../common/enums/firebase-error-code.enum";
import { FirebaseAuthContainer } from "../../../common/containers/auth/firebase-auth-container.util";
import { isAdminPanel } from "../../../common/utilities/routes/routes.util";
import { emailRegex } from "../../../common/utilities/constants/constants";
import { AuthTemplate } from "../../../components/auth-template/auth-template.comp";
import { Loader, LoaderView } from "../../../components/loader/loader.comp";

const TrialFirebaseSignUp = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const invite = searchParams.get("invite");
    const loginLink = !invite ? "/login" : `/login?invite=${invite}`;
    const { signUp, signInWithGoogle, signInWithMicrosoft, providerLoading } =
        FirebaseAuthContainer.useContainer();
    const [login, setLogin] = useState<string>("");
    const [name, setName] = useState<string>("");
    const [organization, setOrganization] = useState<string>("");
    const [ocupation, setOcupation] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [confirmPassword, setConfirmPassword] = useState<string>("");
    const [passwordType, setPasswordType] = useState<string>("password");
    const [confirmPasswordType, setConfirmPasswordType] = useState<string>("password");
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isInvalid, setIsInvalid] = useState<boolean>(false);
    const [checkedTerms, setCheckedTerms] = useState<boolean>(false);

    const handleLoginChange = (evt: ChangeEvent<HTMLInputElement>) => {
        const val = evt.target?.value;
        setLogin(val ?? "");
        setErrorMessage("");
        setIsInvalid(false);
    };

    const handlePasswordChange = (evt: ChangeEvent<HTMLInputElement>) => {
        const val = evt.target?.value;
        setPassword(val ?? "");
        setErrorMessage("");
        setIsInvalid(false);
    };

    const handleConfirmPasswordChange = (evt: ChangeEvent<HTMLInputElement>) => {
        const val = evt.target?.value;
        setConfirmPassword(val ?? "");
        setErrorMessage("");
        setIsInvalid(false);
    };

    const handlePasswordEyeClick = () => {
        setPasswordType(prevValue => (prevValue === "text" ? "password" : "text"));
    };

    const handleConfirmPasswordEyeClick = () => {
        setConfirmPasswordType(prevValue => (prevValue === "text" ? "password" : "text"));
    };

    const handleOrganizationChange = (evt: ChangeEvent<HTMLInputElement>) => {
        const val = evt.target?.value;
        setOrganization(val ?? "");
        setErrorMessage("");
        setIsInvalid(false);
    };

    const handleNameChange = (evt: ChangeEvent<HTMLInputElement>) => {
        const val = evt.target?.value;
        setName(val ?? "");
        setErrorMessage("");
        setIsInvalid(false);
    };

    const handleOcupationChange = (evt: ChangeEvent<HTMLInputElement>) => {
        const val = evt.target?.value;
        setOcupation(val ?? "");
    };

    const handleChangeCheckedTerms = () => {
        setCheckedTerms(value => !value);
    };

    const handleSignUnClick = () => {
        if (!login || !password || !confirmPassword || !organization || !name) return;

        if (!login.match(emailRegex)) {
            setIsInvalid(true);
            setErrorMessage(AuthErrorMessage.invalidEmail);
            return;
        } else if (password !== confirmPassword) {
            setIsInvalid(true);
            setErrorMessage(AuthErrorMessage.differentPasswords);
            return;
        } else if (!invite && !isAdminPanel) {
            setIsInvalid(true);
            setErrorMessage(AuthErrorMessage.invalidInvite);
            return;
        }

        setIsLoading(true);
        signUp(login, password, invite, name, organization, ocupation)
            .catch(error => {
                switch (error.code as FirebaseErrorCode) {
                    case FirebaseErrorCode.emailAlreadyInUse:
                        setIsInvalid(true);
                        setErrorMessage(AuthErrorMessage.emailAlreadyInUse);
                        return;
                    case FirebaseErrorCode.weakPassword:
                        setIsInvalid(true);
                        setErrorMessage(AuthErrorMessage.weakPassword);
                        return;
                    case FirebaseErrorCode.invalidInvite:
                        setIsInvalid(true);
                        setErrorMessage(AuthErrorMessage.invalidInvite);
                        return;
                    default:
                        setErrorMessage(AuthErrorMessage.default);
                }
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleSignInWithGoogleClick = () => {
        if (isLoading) return;

        signInWithGoogle(invite).catch(error => {
            providerErrorHandler(error);
        });
    };

    const handleSignInWithMicrosoftClick = () => {
        if (isLoading) return;

        signInWithMicrosoft(invite).catch(error => {
            providerErrorHandler(error);
        });
    };

    const providerErrorHandler = (error: any) => {
        switch (error.code as FirebaseErrorCode) {
            case FirebaseErrorCode.closePopup:
            case FirebaseErrorCode.cancelPopup:
                break;
            case FirebaseErrorCode.invalidInvite:
                setErrorMessage(AuthErrorMessage.invalidInvite);
                return;
            case FirebaseErrorCode.invalidEmail:
                setErrorMessage(AuthErrorMessage.invalidEmail);
                return;
            case FirebaseErrorCode.forbiddenInvite:
                setErrorMessage(AuthErrorMessage.forbiddenInvite);
                return;
            case FirebaseErrorCode.invalidProvider:
                setErrorMessage(AuthErrorMessage.invalidProvider);
                return;
            case FirebaseErrorCode.accessPermissions:
                navigate(`/error?title=403 Error&description=${AuthErrorMessage.lackPermissions}`);
                return;
            default:
                setErrorMessage(AuthErrorMessage.default);
        }
    };

    return (
        <AuthTemplate>
            <div className="signup">
                <div className="signup-title">Sign Up</div>
                <div className="signup-description">
                    <span className="signup-description__text">Already have an account?</span>
                    <Link to={loginLink} className="signup-description__link">
                        Log In
                    </Link>
                </div>
                <div className="signup-content">
                    <div className="signup-field">
                        <input
                            className="signup-field__input--required"
                            tabIndex={1}
                            type="email"
                            placeholder="Email"
                            onChange={handleLoginChange}
                            value={login}
                        />
                        <div className="signup-field-settings">
                            <span className="signup-field-settings__required">*</span>
                        </div>
                    </div>
                    <div className="signup-field">
                        <input
                            className="signup-field__input--eye-required"
                            tabIndex={2}
                            type={passwordType}
                            placeholder="Password"
                            onChange={handlePasswordChange}
                            value={password}
                        />
                        <div className="signup-field-settings">
                            <SvgIcon
                                className="signup-field-settings__eye"
                                component={passwordType === "text" ? EyeIcon : EyeOffIcon}
                                inheritViewBox
                                onClick={handlePasswordEyeClick}
                            />
                            <span className="signup-field-settings__required">*</span>
                        </div>
                    </div>
                    <div className="signup-field">
                        <input
                            className="signup-field__input--eye-required"
                            tabIndex={3}
                            type={confirmPasswordType}
                            placeholder="Confirm Password"
                            onChange={handleConfirmPasswordChange}
                            value={confirmPassword}
                        />
                        <div className="signup-field-settings">
                            <SvgIcon
                                className="signup-field-settings__eye"
                                component={confirmPasswordType === "text" ? EyeIcon : EyeOffIcon}
                                inheritViewBox
                                onClick={handleConfirmPasswordEyeClick}
                            />
                            <span className="signup-field-settings__required">*</span>
                        </div>
                    </div>
                    <div className="signup-field">
                        <input
                            className="signup-field__input--required"
                            tabIndex={4}
                            type="text"
                            placeholder="Organization"
                            onChange={handleOrganizationChange}
                            value={organization}
                        />
                        <div className="signup-field-settings">
                            <span className="signup-field-settings__required">*</span>
                        </div>
                    </div>
                    <div className="signup-field">
                        <input
                            className="signup-field__input--required"
                            tabIndex={5}
                            type="text"
                            placeholder="Your Name"
                            onChange={handleNameChange}
                            value={name}
                        />
                        <div className="signup-field-settings">
                            <span className="signup-field-settings__required">*</span>
                        </div>
                    </div>
                    <div className="signup-field">
                        <input
                            tabIndex={6}
                            type="text"
                            placeholder="Your Ocupation in the Organization"
                            onChange={handleOcupationChange}
                            value={ocupation}
                        />
                    </div>
                    <div className="signup-terms">
                        <Checkbox
                            className="signup-terms__checkbox"
                            checked={checkedTerms}
                            onChange={handleChangeCheckedTerms}
                            icon={<CheckboxIcon />}
                            checkedIcon={<CheckboxCheckedIcon />}
                        />
                        <span className="signup-terms__text">I accept the</span>
                        <a className="signup-terms__link">terms</a>
                        <span className="signup-terms__text">&</span>
                        <a className="signup-terms__link">privacy policy</a>
                    </div>
                    <div className="signup-message">
                        <span>{errorMessage}</span>
                    </div>
                    <div className="signin-separator"></div>
                    <div className="signin-providers">
                        <Button
                            className="signin-providers__button"
                            variant="outlined"
                            onClick={handleSignInWithGoogleClick}
                            startIcon={<SvgIcon component={GoogleIcon} inheritViewBox />}
                        >
                            Continue with Google
                        </Button>
                        <Button
                            className="signin-providers__button"
                            variant="outlined"
                            onClick={handleSignInWithMicrosoftClick}
                            startIcon={<SvgIcon component={MicrosoftIcon} inheritViewBox />}
                        >
                            Continue with Microsoft
                        </Button>
                    </div>
                </div>
                <div className="signup-action">
                    <Button
                        className="primary-button"
                        variant="text"
                        onClick={handleSignUnClick}
                        disabled={
                            !login ||
                            !organization ||
                            !name ||
                            !password ||
                            !confirmPassword ||
                            isLoading ||
                            isInvalid ||
                            !checkedTerms
                        }
                    >
                        {!isLoading ? <span>Sign up</span> : <Loader size={24} borderWidth={4} />}
                    </Button>
                </div>
                <LoaderView show={providerLoading} />
            </div>
        </AuthTemplate>
    );
};

export default TrialFirebaseSignUp;
